/**
 * EmailTextEditor — wiederverwendbares „E-Mail-Text ansehen / ändern"-Bauteil
 * (Briefsymbol). Öffnet einen Dialog mit Textarea zum Bearbeiten des Begleittexts
 * einer manuell ausgelösten E-Mail. Pflicht-/Sicherheitsblöcke (Fahrzeugdetails,
 * Portal-Link) werden serverseitig angehängt und sind hier bewusst nicht editierbar.
 *
 * Props:
 *  - value, onChange: kontrollierter Begleittext
 *  - immutableHint: Hinweistext zu den fix angehängten Blöcken
 *  - label: Button-Beschriftung (Default „E-Mail-Text ansehen / ändern")
 *  - onGenerateDraft: optional async () => string  (KI-Erstentwurf)
 *  - testid: Basis-Test-ID
 */
import { useState } from "react";
import { Mail, Loader2, Wand2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export default function EmailTextEditor({
    value, onChange, immutableHint,
    label = "E-Mail-Text ansehen / ändern",
    onGenerateDraft, testid = "email-text-editor", rows = 7,
    defaultText = "",
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value || defaultText || "");
    const [gen, setGen] = useState(false);

    const openModal = () => { setDraft(value || defaultText || ""); setOpen(true); };
    const save = () => { onChange?.(draft); setOpen(false); };

    const generate = async () => {
        if (!onGenerateDraft) return;
        setGen(true);
        try {
            const text = await onGenerateDraft();
            if (text) setDraft(text);
        } finally { setGen(false); }
    };

    return (
        <>
            <button type="button" onClick={openModal}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#111827] hover:underline"
                data-testid={`${testid}-toggle`}>
                <Mail className="w-3.5 h-3.5" /> {label}
                {value?.trim() ? <span className="ml-1 text-[9px] font-black uppercase bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm">angepasst</span> : null}
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid={`${testid}-modal`}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#FFCC00]" /> E-Mail-Text</DialogTitle>
                        <DialogDescription>
                            {defaultText
                                ? "Der Standardtext ist voreingestellt – Sie können ihn anpassen oder unverändert übernehmen."
                                : "Passen Sie den Begleittext an. Leer lassen = Standardtext der Plattform."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        {onGenerateDraft && (
                            <Button type="button" onClick={generate} variant="outline" disabled={gen}
                                className="h-8 rounded-sm text-xs font-bold" data-testid={`${testid}-generate`}>
                                {gen ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                                KI-Erstentwurf
                            </Button>
                        )}
                        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={rows}
                            className="text-sm" placeholder="Begleittext (optional). Leer lassen = Standardtext."
                            data-testid={`${testid}-text`} />
                        {immutableHint && (
                            <p className="text-[11px] text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-sm p-2">
                                {immutableHint}
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Abbrechen</Button>
                        <Button type="button" className="rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold" onClick={save} data-testid={`${testid}-save`}>Übernehmen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
